import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { PageHead } from '../../shell/PageHead';
import { Button } from '../../ui/Button';
import { Badge } from '../../ui/Badge';
import { Modal } from '../../ui/Modal';

interface RequestLog {
  id: string;
  company_id: string;
  params_json: any;
  resultado_resumo: string | null;
  providers_tentados: any;
  files_retornados: number;
  tempo_total_ms: number;
  erro_codigo: string | null;
  erro_msg: string | null;
  ranker_used: string | null;
  ranker_score_top: number | null;
  ranker_escalated: boolean;
  ranker_aborted: boolean;
  ranker_degraded_from: string | null;
  criada_em: string | null;
}

function relativeTime(iso: string | null) {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'agora';
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d`;
}

function paramsResumo(p: any): string {
  if (!p || typeof p !== 'object') return '—';
  let parsed = p;
  if (typeof p === 'string') {
    try { parsed = JSON.parse(p); } catch { return '—'; }
  }
  const parts: string[] = [];
  if (parsed.keywords) parts.push(Array.isArray(parsed.keywords) ? parsed.keywords.join(' ') : String(parsed.keywords));
  if (parsed.tipo) parts.push(`tipo=${parsed.tipo}`);
  if (parsed.ranker && parsed.ranker !== 'auto') parts.push(`ranker=${parsed.ranker}`);
  if (parsed.human_revision) parts.push('hr');
  return parts.join(' · ') || '—';
}

export function Logs() {
  const { apiFetch, impersonate } = useAuth();
  const [items, setItems] = useState<RequestLog[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'true' | 'false'>('all');
  const [detail, setDetail] = useState<RequestLog | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams();
    if (filter !== 'all') params.set('erro', filter);
    params.set('limit', '50');
    const r = await apiFetch<{ items: RequestLog[]; total: number }>(`/v1/admin/requests/?${params}`);
    setLoading(false);
    if (!r.ok || !r.data) {
      setError(r.error?.message ?? 'Falha ao carregar');
      return;
    }
    setItems(r.data.items);
    setTotal(r.data.total);
  }, [apiFetch, filter]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <>
      <PageHead
        title="Logs"
        eyebrow="REQUESTS"
        desc={impersonate ? `Requests da empresa "${impersonate.nome}".` : 'Histórico de chamadas à API.'}
        actions={
          <Button variant="ghost" icon="refresh" size="sm" onClick={load}>
            Atualizar
          </Button>
        }
      />

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginBottom: 14,
          padding: '10px 14px',
          background: 'var(--bg-1)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--r-md)'
        }}
      >
        <SegmentedFilter
          value={filter}
          onChange={(v) => setFilter(v as any)}
          options={[
            { value: 'all', label: 'Todos' },
            { value: 'false', label: 'Sucesso' },
            { value: 'true', label: 'Erros' }
          ]}
        />
        <span className="tnum" style={{ fontSize: 12.5, color: 'var(--text-3)', marginLeft: 'auto' }}>
          {total} requests
        </span>
      </div>

      {error && (
        <div
          style={{
            background: 'var(--danger-bg)',
            color: 'var(--danger)',
            border: '1px solid color-mix(in oklch, var(--danger) 30%, transparent)',
            borderRadius: 'var(--r-sm)',
            padding: '10px 14px',
            fontSize: 13.5,
            marginBottom: 14
          }}
        >
          {error}
        </div>
      )}

      {loading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="skeleton" style={{ height: 46 }} />
          ))}
        </div>
      )}

      {!loading && !error && items.length === 0 && (
        <div
          className="grid-bg"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            padding: '64px 24px',
            borderRadius: 'var(--r-lg)',
            border: '1px dashed var(--border)',
            gap: 12,
            minHeight: 280,
            justifyContent: 'center'
          }}
        >
          <h3 style={{ fontSize: 15.5, marginBottom: 6 }}>Sem requests no período</h3>
          <p style={{ fontSize: 13.5, color: 'var(--text-2)' }}>
            Chamadas à API (/v1/search) aparecem aqui em tempo real.
          </p>
        </div>
      )}

      {!loading && !error && items.length > 0 && (
        <div
          style={{
            background: 'var(--bg-1)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--r-lg)',
            overflow: 'hidden'
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--bg-2)', textAlign: 'left' }}>
                <th style={{ padding: '10px 14px', fontSize: 11.5, fontWeight: 600, color: 'var(--text-2)' }}>Quando</th>
                <th style={{ padding: '10px 14px', fontSize: 11.5, fontWeight: 600, color: 'var(--text-2)' }}>Params</th>
                <th style={{ padding: '10px 14px', fontSize: 11.5, fontWeight: 600, color: 'var(--text-2)' }}>Ranking</th>
                <th style={{ padding: '10px 14px', fontSize: 11.5, fontWeight: 600, color: 'var(--text-2)' }}>Resultado</th>
                <th style={{ padding: '10px 14px', fontSize: 11.5, fontWeight: 600, color: 'var(--text-2)' }}>Files</th>
                <th style={{ padding: '10px 14px', fontSize: 11.5, fontWeight: 600, color: 'var(--text-2)' }}>Tempo</th>
                <th style={{ padding: '10px 14px', fontSize: 11.5, fontWeight: 600, color: 'var(--text-2)' }} />
              </tr>
            </thead>
            <tbody>
              {items.map((r) => {
                const hasError = !!r.erro_codigo;
                return (
                  <tr key={r.id} style={{ borderTop: '1px solid var(--border-soft)' }}>
                    <td style={{ padding: '10px 14px', color: 'var(--text-2)', whiteSpace: 'nowrap' }}>
                      {relativeTime(r.criada_em)}
                    </td>
                    <td style={{ padding: '10px 14px', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} className="mono">
                      {paramsResumo(r.params_json)}
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      {r.ranker_used ? (
                        <Badge color={r.ranker_escalated ? 'violet' : 'neutral'} size="sm" mono>
                          {r.ranker_used}
                          {r.ranker_score_top !== null ? ` · ${r.ranker_score_top.toFixed(2)}` : ''}
                        </Badge>
                      ) : r.ranker_degraded_from ? (
                        <Badge color="warning" size="sm" mono>
                          degraded:{r.ranker_degraded_from}
                        </Badge>
                      ) : (
                        <span style={{ color: 'var(--text-3)', fontSize: 12 }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      {hasError ? (
                        <Badge color="danger" dot size="sm">
                          {r.erro_codigo}
                        </Badge>
                      ) : (
                        <span className="mono" style={{ fontSize: 11.5, color: 'var(--text-1)' }}>
                          {r.resultado_resumo ?? '—'}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '10px 14px' }} className="tnum">
                      {r.files_retornados}
                    </td>
                    <td style={{ padding: '10px 14px' }} className="tnum">
                      {r.tempo_total_ms}ms
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                      <Button variant="ghost" size="sm" onClick={() => setDetail(r)}>
                        Ver
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={!!detail}
        onClose={() => setDetail(null)}
        title="Detalhes da request"
        subtitle={detail ? relativeTime(detail.criada_em) + ' · ' + detail.id.slice(0, 8) : ''}
        width={620}
        footer={
          <Button variant="default" onClick={() => setDetail(null)}>
            Fechar
          </Button>
        }
      >
        {detail && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, fontSize: 13 }}>
            <Section label="Params">
              <pre style={preStyle}>{JSON.stringify(typeof detail.params_json === 'string' ? JSON.parse(detail.params_json) : detail.params_json, null, 2)}</pre>
            </Section>
            <Section label="Ranking">
              <Row k="ranker_used" v={detail.ranker_used ?? '—'} />
              <Row k="score top" v={detail.ranker_score_top !== null ? detail.ranker_score_top.toFixed(4) : '—'} />
              <Row k="escalated" v={String(detail.ranker_escalated)} />
              <Row k="aborted" v={String(detail.ranker_aborted)} />
              <Row k="degraded_from" v={detail.ranker_degraded_from ?? '—'} />
            </Section>
            <Section label="Resultado">
              <Row k="resultado_resumo" v={detail.resultado_resumo ?? '—'} />
              <Row k="files_retornados" v={String(detail.files_retornados)} />
              <Row k="tempo_total_ms" v={`${detail.tempo_total_ms}ms`} />
              {detail.erro_codigo && (
                <>
                  <Row k="erro_codigo" v={detail.erro_codigo} danger />
                  <Row k="erro_msg" v={detail.erro_msg ?? '—'} danger />
                </>
              )}
            </Section>
            {detail.providers_tentados && (
              <Section label="Providers tentados">
                <pre style={preStyle}>
                  {JSON.stringify(typeof detail.providers_tentados === 'string' ? JSON.parse(detail.providers_tentados) : detail.providers_tentados, null, 2)}
                </pre>
              </Section>
            )}
          </div>
        )}
      </Modal>
    </>
  );
}

const preStyle: React.CSSProperties = {
  background: 'var(--bg-0)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--r-sm)',
  padding: '10px 12px',
  fontSize: 11.5,
  fontFamily: 'var(--font-mono)',
  color: 'var(--text-1)',
  margin: 0,
  overflow: 'auto',
  maxHeight: 220,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word'
};

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="eyebrow" style={{ marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>{children}</div>
    </div>
  );
}

function Row({ k, v, danger }: { k: string; v: string; danger?: boolean }) {
  return (
    <div style={{ display: 'flex', gap: 10, fontSize: 12.5 }}>
      <span className="mono" style={{ color: 'var(--text-3)', minWidth: 130 }}>
        {k}
      </span>
      <span className="mono" style={{ color: danger ? 'var(--danger)' : 'var(--text-1)' }}>
        {v}
      </span>
    </div>
  );
}

function SegmentedFilter({
  value,
  onChange,
  options
}: {
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <div
      style={{
        display: 'inline-flex',
        background: 'var(--bg-2)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-sm)',
        padding: 2,
        gap: 2
      }}
    >
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            onClick={() => onChange(o.value)}
            style={{
              padding: '5px 12px',
              fontSize: 12.5,
              fontWeight: 500,
              borderRadius: 4,
              border: 'none',
              background: active ? 'var(--bg-0)' : 'transparent',
              color: active ? 'var(--text-0)' : 'var(--text-2)',
              boxShadow: active ? 'var(--shadow-1)' : 'none',
              transition: 'all 0.14s var(--ease)',
              cursor: 'pointer'
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
